import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { fetchMyContext } from '../api/context';
import { checkPermission, permissionKey, VERTICAL_SLICE_1_PERMISSION_CHECKS } from '../api/permissions';
import type { MyContext, PermissionAction, PermissionResource, UUID } from '../types/domain';

/**
 * Server-resolved identity, tenant, roles, and permission set. This
 * REPLACES `web-preview/index.html`'s `ROLE_NAV_ACCESS` — that map was
 * always explicitly labeled "DEMO PERMISSION VIEW — NOT YET SERVER
 * ENFORCED" and is intentionally NOT reused, extended, or imported here.
 * Every value exposed by this context comes from a real RPC call to
 * `fn_get_my_context()` / `fn_check_permission()` (migration 0032) — nothing
 * here is a client-side guess about what a role is "supposed to" be allowed
 * to do.
 *
 * `hasPermission()` reads from a prefetched, server-checked map (fail-closed
 * — defaults to false while loading or for any resource/action not in the
 * prefetch list). `checkPermissionNow()` is provided for one-off,
 * branch-specific pre-flight checks (e.g. "can I approve THIS PR, which
 * belongs to branch X") that go straight to the server rather than the
 * tenant-wide prefetched cache. In both cases, the true enforcement is still
 * server-side inside the permission-gated RPC functions themselves
 * (api/purchasing.ts, api/masterData.ts) — this context only controls what
 * the UI shows, never what the server allows.
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md §8k.
 */

interface PermissionState {
  context: MyContext | null;
  loading: boolean;
  error: string | null;
  /** Fail-closed: true only once loaded AND the server said yes. */
  hasPermission: (resource: PermissionResource, action: PermissionAction) => boolean;
  /** Live, one-off server check — use before a branch-specific mutation. */
  checkPermissionNow: (resource: PermissionResource, action: PermissionAction, branchId?: UUID | null) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export const PermissionContext = createContext<PermissionState | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const [context, setContext] = useState<MyContext | null>(null);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Tracks whether we have ever completed a successful resolution in this
  // browser session. Used only to distinguish "first load, nothing
  // server-verified yet" (fail-closed) from "a later background refresh
  // — e.g. supabase's automatic access-token rotation — is in flight while
  // an already-verified permission set is still valid" (keep showing the
  // last server-verified result instead of flashing a false denial).
  // Server-side permission enforcement inside every fn_*_checked RPC is
  // unaffected either way — this only controls what the UI shows.
  const hasResolvedOnce = useRef(false);

  const load = async () => {
    if (!session) {
      // No session at all -> fail-closed immediately, no RPC call needed.
      setContext({ authenticated: false });
      setPermissions({});
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ctx = await fetchMyContext();
      setContext(ctx);

      if (ctx.authenticated) {
        // Prefetch the full vertical-slice-1 permission set in parallel —
        // one real fn_check_permission() RPC call per (resource, action)
        // pair, not a client-side reimplementation of the role matrix.
        const results = await Promise.all(
          VERTICAL_SLICE_1_PERMISSION_CHECKS.map(async ({ resource, action }) => {
            const allowed = await checkPermission(resource, action, null);
            return [permissionKey(resource, action), allowed] as const;
          })
        );
        setPermissions(Object.fromEntries(results));
      } else {
        setPermissions({});
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      // Fail-closed on any error — never leave stale "allowed" entries around.
      setContext({ authenticated: false });
      setPermissions({});
    } finally {
      setLoading(false);
      hasResolvedOnce.current = true;
    }
  };

  useEffect(() => {
    if (authLoading) return; // wait for AuthContext to resolve session restore first
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token, authLoading]);

  const hasPermission = (resource: PermissionResource, action: PermissionAction): boolean => {
    // Fail-closed only for the true first resolution in this session —
    // never treat "haven't heard from the server yet" as an answer. Once a
    // server-verified permission set exists, a later background reload
    // (e.g. token refresh) keeps serving that last-verified result while
    // the new one is in flight, instead of flashing a false denial.
    if (loading && !hasResolvedOnce.current) return false;
    return permissions[permissionKey(resource, action)] === true;
  };

  const checkPermissionNow = async (
    resource: PermissionResource,
    action: PermissionAction,
    branchId: UUID | null = null
  ): Promise<boolean> => checkPermission(resource, action, branchId);

  const value = useMemo<PermissionState>(
    () => ({ context, loading, error, hasPermission, checkPermissionNow, refresh: load }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [context, permissions, loading, error]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions(): PermissionState {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions() must be called within <PermissionProvider>');
  }
  return ctx;
}
